/* Copyright (c) 2020-present CloudIO Inc */

import {
  hasWSError,
  SubscriptionEnterRequest,
  SubscriptionEvent,
} from '@cloudio-saas/datasource-types';
import {
  useLatest,
  useStateXRemover,
  useStateXSetter,
} from '@cloudio-saas/statex';
import { useEffect, useRef } from 'react';
import { useWS } from '../streams/useWebSocket';

export default function useSubscription(
  appUid: string,
  onEvent: (event: SubscriptionEvent) => void,
  ctx?: string | null,
  id?: string | null,
  ts?: string | null,
): void {
  const { sendRequest, addListener, addReconnectListener } = useWS();
  const idRef = useRef('');
  const enteredRef = useRef(false);
  const set = useStateXSetter();
  const remove = useStateXRemover();
  const onEventRef = useLatest(onEvent);

  useEffect(() => {
    if (!(ctx && id)) {
      return;
    }
    idRef.current = '';
    const req: SubscriptionEnterRequest = {
      ts: ts || new Date().toISOString(),
      ctx,
      id,
      type: 'SubscriptionEnter',
    };
    const unlisten = addListener((message) => {
      switch (message.type) {
        case 'SubscriptionEvent': {
          const { event } = message;
          if (event.id === id && event.ctx === ctx && event.appUid === appUid) {
            onEventRef.current(event);
          }
          break;
        }
        default:
      }
    });

    if (req.id && !enteredRef.current) {
      enteredRef.current = true;
      // avoid sending request with empty pageId
      sendRequest(req, { appUid })[0].then((response) => {
        if (!hasWSError(response)) {
          if (!enteredRef.current) {
            // already unmounted! hence send Exit back
            sendRequest(
              { type: 'SubscriptionExit', _id: response.data._id as string },
              { appUid },
            );
          } else {
            idRef.current = response.data._id as string;
          }
        }
      });
    }
    // const timer = setInterval(() => {
    //   sendUpdate();
    // }, 60000);

    const unlisten2 = addReconnectListener(() => {
      if (idRef.current) {
        sendRequest(
          { type: 'SubscriptionExit', _id: idRef.current },
          { appUid },
        );
      }
      sendRequest(req, { appUid })[0].then((response) => {
        if (!hasWSError(response)) {
          idRef.current = response.data._id as string;
        }
      });
    });

    return () => {
      unlisten();
      unlisten2();
      // if (timer) {
      //   clearInterval(timer);
      // }
      if (idRef.current) {
        sendRequest(
          { type: 'SubscriptionExit', _id: idRef.current },
          { appUid },
        );
        idRef.current = '';
      } else {
        // unmounting before getting the enter response
      }
      enteredRef.current = false;
    };
  }, [
    addListener,
    addReconnectListener,
    appUid,
    ctx,
    id,
    onEventRef,
    remove,
    sendRequest,
    set,
    ts,
  ]);
}
